import type { ChecklistItem, ChecklistRun, Room, RunItem } from '@dia/db';

/**
 * The rules of a cleaning job, as pure functions.
 *
 * The screen only draws what these return, so the order a crew member works in
 * is testable without a phone. The order is the one the housekeeping app
 * taught the crew (Section 7):
 *
 *   1. the opening walk of the house,
 *   2. room by room, and inside a room: the "anything wrong?" question first
 *      (damage photos before anything is touched), then the laundry, then the
 *      rest in the order the checklist gives,
 *   3. the mop, house wide, after every room is done,
 *   4. the closing cards.
 */

export type Pair = { item: ChecklistItem; room: Room | null };

export type CardStage = 'open' | 'room' | 'mop' | 'close';

export type Card = {
  /** item id plus room id: the same item in two bedrooms is two cards. */
  key: string;
  item: ChecklistItem;
  room: Room | null;
  stage: CardStage;
};

export const PROBLEM_NO = 'problem:no';
export const PROBLEM_YES = 'problem:yes';

export function cardKey(itemId: string, roomId: string | null): string {
  return `${itemId}:${roomId ?? 'casa'}`;
}

/** Laundry lines carry a ref that starts with L, the tag from the printed manual revisions. */
export function isLaundryItem(item: Pick<ChecklistItem, 'ref'>): boolean {
  return /^L\d/i.test(item.ref ?? '');
}

/**
 * The mop step. It is recognised by the verb it starts with, so "wash the
 * mops" (a closing chore) is not mistaken for it.
 */
export function isMopItem(item: Pick<ChecklistItem, 'text_es' | 'text_en'>): boolean {
  return /^\s*(trapea|trapear)\b/i.test(item.text_es) || /^\s*mop\b/i.test(item.text_en);
}

function rank(item: ChecklistItem): number {
  if (item.ask_problem) return 0;
  if (isLaundryItem(item)) return 1;
  return 2;
}

function byItemOrder(a: Pair, b: Pair): number {
  return rank(a.item) - rank(b.item) || a.item.sort - b.item.sort || a.item.ref?.localeCompare(b.item.ref ?? '') || 0;
}

function roomOrder(a: Room | null, b: Room | null): number {
  if (!a || !b) return 0;
  return a.sort - b.sort || a.slug.localeCompare(b.slug);
}

export function orderCards(pairs: Pair[]): Card[] {
  const open: Pair[] = [];
  const close: Pair[] = [];
  const mop: Pair[] = [];
  const rooms = new Map<string, Pair[]>();
  const roomById = new Map<string, Room>();

  for (const pair of pairs) {
    const kind = pair.item.applies_to_room_kind;
    if (pair.room === null && kind === 'house_close') {
      (isMopItem(pair.item) ? mop : close).push(pair);
    } else if (pair.room === null) {
      open.push(pair);
    } else if (isMopItem(pair.item)) {
      mop.push(pair);
    } else {
      const list = rooms.get(pair.room.id) ?? [];
      list.push(pair);
      rooms.set(pair.room.id, list);
      roomById.set(pair.room.id, pair.room);
    }
  }

  const roomsInOrder = [...roomById.values()].sort(roomOrder);
  const make = (list: Pair[], stage: CardStage): Card[] =>
    list.map(({ item, room }) => ({ key: cardKey(item.id, room?.id ?? null), item, room, stage }));

  const ordered: Card[] = [];
  ordered.push(...make([...open].sort(byItemOrder), 'open'));
  for (const room of roomsInOrder) {
    ordered.push(...make([...(rooms.get(room.id) ?? [])].sort(byItemOrder), 'room'));
  }
  // The mop pass walks the rooms in the same order, then the house wide line.
  const mopSorted = [...mop].sort(
    (a, b) => (a.room === null ? 1 : 0) - (b.room === null ? 1 : 0) || roomOrder(a.room, b.room) || a.item.sort - b.item.sort,
  );
  ordered.push(...make(mopSorted, 'mop'));
  ordered.push(...make([...close].sort((a, b) => a.item.sort - b.item.sort), 'close'));
  return ordered;
}

export type RunItemLike = Pick<
  RunItem,
  'client_id' | 'run_id' | 'item_id' | 'room_id' | 'done_at' | 'photo_path' | 'note'
>;

export type CardState = {
  done: boolean;
  photoPath: string | null;
  doneAt: string | null;
  /** Only for the "anything wrong?" card. null until answered. */
  problem: boolean | null;
  note: string | null;
};

const EMPTY: CardState = { done: false, photoPath: null, doneAt: null, problem: null, note: null };

export function emptyState(): CardState {
  return { ...EMPTY };
}

/**
 * Run items are append only, so a card can have several rows: a tick, then a
 * photo that arrived later, then a repeat after a redo. The card is done when
 * any row exists, and it has a photo when any row carries one.
 */
export function deriveStates(rows: RunItemLike[]): Map<string, CardState> {
  const states = new Map<string, CardState>();
  const ordered = [...rows].sort((a, b) => a.done_at.localeCompare(b.done_at));

  for (const row of ordered) {
    const key = cardKey(row.item_id, row.room_id);
    const state = states.get(key) ?? emptyState();
    state.done = true;
    state.doneAt = row.done_at;
    if (row.photo_path) state.photoPath = row.photo_path;
    if (row.note === PROBLEM_YES) state.problem = true;
    else if (row.note === PROBLEM_NO) state.problem = false;
    else if (row.note) state.note = row.note;
    states.set(key, state);
  }
  return states;
}

/** A card wants a photo when the checklist asks for one, or when the answer was "yes, something is wrong". */
export function needsPhoto(card: Card, state: CardState): boolean {
  return card.item.photo_required || (card.item.ask_problem && state.problem === true);
}

export function isComplete(card: Card, state: CardState): boolean {
  if (!state.done) return false;
  return !needsPhoto(card, state) || state.photoPath !== null;
}

export type Progress = {
  total: number;
  done: number;
  /** Cards ticked whose required photo has not been taken yet. */
  missingPhotos: number;
  /** Cards not ticked at all. */
  open: number;
  finished: boolean;
};

export function progress(cards: Card[], states: Map<string, CardState>): Progress {
  let done = 0;
  let missingPhotos = 0;
  let open = 0;
  for (const card of cards) {
    const state = states.get(card.key) ?? EMPTY;
    if (isComplete(card, state)) done += 1;
    else if (state.done) missingPhotos += 1;
    else open += 1;
  }
  return { total: cards.length, done, missingPhotos, open, finished: cards.length > 0 && done === cards.length };
}

/** The first card that still needs something, so "what next" always has an answer. */
export function nextCard(cards: Card[], states: Map<string, CardState>): Card | null {
  return cards.find((card) => !isComplete(card, states.get(card.key) ?? EMPTY)) ?? null;
}

export type RunPhase = 'working' | 'waiting' | 'redo' | 'approved';

/**
 * Where a job stands. A crew member cannot change review_status (the database
 * refuses it), so a redo is answered by finishing again: completed_at moves
 * past reviewed_at, and that is how the reviewer sees the job is back.
 */
export function runPhase(
  run: Pick<ChecklistRun, 'completed_at' | 'review_status' | 'reviewed_at'>,
): RunPhase {
  if (run.review_status === 'approved') return 'approved';
  if (!run.completed_at) return run.review_status === 'redo' ? 'redo' : 'working';
  if (run.review_status === 'redo') {
    const resubmitted = !run.reviewed_at || run.completed_at > run.reviewed_at;
    return resubmitted ? 'waiting' : 'redo';
  }
  return 'waiting';
}

/** A run a reviewer still has to look at. */
export function needsReview(run: Pick<ChecklistRun, 'completed_at' | 'review_status' | 'reviewed_at'>): boolean {
  return runPhase(run) === 'waiting';
}

/** The minimum shape of an outbox row, so this file needs no database to be tested. */
export type PendingOp = {
  table: string;
  kind: 'insert' | 'update';
  payload: Record<string, unknown>;
  match?: Record<string, unknown>;
  seq?: number;
  id?: string;
};

/**
 * What the phone knows that the island may not yet: rows still in the outbox.
 * Merged over the server rows, so a tick made in airplane mode shows at once.
 * A row that has landed in the meantime is not counted twice (client_id).
 */
export function mergePendingRunItems(server: RunItemLike[], ops: PendingOp[], runId: string): RunItemLike[] {
  const seen = new Set(server.map((r) => r.client_id));
  const merged = [...server];
  for (const op of ops) {
    if (op.table !== 'run_items' || op.kind !== 'insert' || op.payload.run_id !== runId) continue;
    const clientId = String(op.payload.client_id ?? op.id ?? '');
    if (!clientId || seen.has(clientId)) continue;
    seen.add(clientId);
    merged.push({
      client_id: clientId,
      run_id: runId,
      item_id: String(op.payload.item_id),
      room_id: (op.payload.room_id as string | null | undefined) ?? null,
      done_at: String(op.payload.done_at ?? new Date().toISOString()),
      photo_path: (op.payload.photo_path as string | null | undefined) ?? null,
      note: (op.payload.note as string | null | undefined) ?? null,
    });
  }
  return merged;
}

/** Applies the pending start and finish of a run over what the server last said. */
export function applyPendingToRun(
  run: ChecklistRun | null,
  ops: PendingOp[],
  runId: string,
): ChecklistRun | null {
  let current = run;
  for (const op of [...ops].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))) {
    if (op.table !== 'checklist_runs') continue;
    if (op.kind === 'insert' && op.payload.id === runId && !current) {
      current = {
        id: runId,
        checklist_id: String(op.payload.checklist_id),
        stay_id: (op.payload.stay_id as string | null | undefined) ?? null,
        house_id: (op.payload.house_id as string | null | undefined) ?? null,
        assigned_to: (op.payload.assigned_to as string | null | undefined) ?? null,
        started_at: (op.payload.started_at as string | null | undefined) ?? null,
        completed_at: null,
        reviewed_by: null,
        reviewed_at: null,
        review_status: 'pending',
        review_note_es: null,
        review_note_en: null,
        client_id: runId,
        created_at: String(op.payload.started_at ?? new Date().toISOString()),
        updated_at: String(op.payload.started_at ?? new Date().toISOString()),
      };
    } else if (op.kind === 'update' && current && (op.match?.client_id === current.client_id || op.match?.id === current.id)) {
      current = { ...current, ...(op.payload as Partial<ChecklistRun>) };
    }
  }
  return current;
}
